import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Heart, Star, Truck, ShieldCheck, RotateCcw, Check, DollarSign, Flame, Share2, Copy, ShoppingBag, Store, ArrowRight, ArrowLeft } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { resolveImage } from "../lib/image";
import { fmt } from "../lib/money";
import { canSeeAffiliateUI } from "../lib/permissions";

import { WhatsAppButton, WhatsAppFloating } from "../components/WhatsAppButton";
import ProductVideo from "../components/ProductVideo";

function ShareToEarnBlock({ product }) {
  const { user } = useAuth();
  const [slug, setSlug] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!user) return;
    api.get("/affiliate/me").then(({ data }) => { if (alive) setSlug(data.slug); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  // Path-based affiliate URL (`/api/r/{slug}/products/{id}`) beats the older
  // query-based one (`/api/r/{slug}?to=/products/{id}`) for one reason:
  // some link-preview services (matrixvideo.online, generic unfurlers)
  // dedupe cached shop-item cards by product path, ignoring query params.
  // A structurally-unique URL per (affiliate, product) forces them to
  // scrape a fresh preview for each affiliate. Backend serves both formats.
  // `v=<day>` cache-busts the preview services' own preview cache so image /
  // title edits propagate.
  const cacheKey = String(Math.floor(Date.now() / 86400000));
  const link = slug
    ? `${window.location.origin}/api/r/${slug}/products/${product.product_id}?v=${cacheKey}`
    : null;

  const share = async () => {
    if (!link) { toast.error("Sign in to get your affiliate link."); return; }
    const text = `${product.title} on Guyana Discounts — ${fmt(product.price)}`;
    if (navigator.share) {
      try { await navigator.share({ title: product.title, text, url: link }); return; } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Affiliate link copied to clipboard.");
    } catch {
      toast.error("Could not copy. Long-press the link below.");
    }
  };

  // What the affiliate would actually pocket per sale of this product —
  // displayed as a big bold dollar figure to drive share-button clicks.
  const earnPerSale = Math.round(
    (product.price || 0) * (product.affiliate_commission_pct || 0) / 100
  );

  return (
    <div className="mt-4">
      <button
        data-testid="share-to-earn-btn"
        type="button"
        onClick={share}
        className="w-full min-h-[3.5rem] py-2.5 bg-[#00A355] hover:bg-[#008844] text-white font-semibold flex items-center justify-between gap-2 px-4 sm:px-5 transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          <DollarSign className="w-5 h-5 shrink-0" />
          <span className="text-left min-w-0 flex-1">
            <div className="font-display font-black text-sm sm:text-base leading-tight flex items-center gap-2 flex-wrap">
              <span>Share to earn</span>
              {earnPerSale > 0 && (
                <span
                  data-testid="share-earn-amount"
                  className="font-display font-black text-base sm:text-lg bg-[#FFE600] text-black px-2 py-0.5 leading-none shadow-[2px_2px_0_0_#000] animate-pulse"
                  style={{ animationDuration: "2s" }}
                >
                  {fmt(earnPerSale)}
                </span>
              )}
            </div>
            <div className="text-[11px] sm:text-xs opacity-90 font-normal leading-snug mt-1">
              {product.affiliate_commission_pct}% commission per sale · paid into your account
            </div>
          </span>
        </span>
        <span className="text-[10px] sm:text-xs uppercase tracking-widest opacity-80 shrink-0">FB · WhatsApp →</span>
      </button>
      {!user && (
        <p className="mt-2 text-[11px] text-neutral-500">
          <Link to="/login" className="underline">Sign in</Link> to get your personal affiliate link.
        </p>
      )}
      {user && slug && (
        <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2 text-[11px]">
          <code className="font-mono bg-neutral-100 px-2 py-1 text-neutral-600 truncate flex-1 min-w-0">{link}</code>
          <Link to="/affiliate" className="text-[#0033FF] underline whitespace-nowrap self-start sm:self-auto">dashboard →</Link>
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [recs, setRecs] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [justAdded, setJustAdded] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [wish, setWish] = useState(false);
  const [posOrderOpen, setPosOrderOpen] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();

  const load = () => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(null));
    api.get(`/reviews/${id}`).then((r) => setReviews(r.data)).catch(() => {});
    api.get(`/products/${id}/recommendations?limit=8`).then((r) => setRecs(r.data)).catch(() => setRecs([]));
  };
  useEffect(() => { load(); }, [id]);

  // Fire-and-forget view tracking — feeds the recommendation algorithm.
  useEffect(() => {
    if (!id) return;
    api.post(`/products/${id}/view`).catch(() => {});
  }, [id]);

  // Dynamically inject product-specific OG / Twitter meta tags into <head>
  // so JS-aware crawlers (Twitter, Discord, Telegram preview) and the user's
  // browser share-sheet pick up the right title / image / description. Note:
  // Facebook and WhatsApp do NOT run JS — they only see the static index.html
  // tags, which is why we ALSO expose /api/share/products/{id} as the
  // canonical "share URL" for those platforms.
  useEffect(() => {
    if (!product) return;
    const img = (product.images && product.images[0]) || "";
    const absImg = img.startsWith("http") ? img : `${window.location.origin}${img}`;
    const desc = (product.description || "").slice(0, 200).replace(/\s+/g, " ").trim();
    const tags = [
      ["property", "og:type", "product"],
      ["property", "og:title", product.title],
      ["property", "og:description", `G$${Math.round(product.price).toLocaleString()} · ${desc}`],
      ["property", "og:image", absImg],
      ["property", "og:image:secure_url", absImg],
      ["property", "og:image:alt", product.title],
      ["property", "og:url", `${window.location.origin}/products/${product.product_id}`],
      ["name", "twitter:card", "summary_large_image"],
      ["name", "twitter:title", product.title],
      ["name", "twitter:description", desc],
      ["name", "twitter:image", absImg],
    ];
    const cleanup = [];
    tags.forEach(([attr, key, value]) => {
      if (!value) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const prev = el ? el.getAttribute("content") : null;
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
      cleanup.push({ el, prev, created });
    });
    document.title = `${product.title} · Guyana Discounts`;
    return () => {
      cleanup.forEach(({ el, prev, created }) => {
        if (created) {
          el.remove();
        } else if (prev !== null) {
          el.setAttribute("content", prev);
        }
      });
    };
  }, [product]);

  useEffect(() => {
    if (!user) return;
    api.get("/wishlist").then((r) => {
      setWish(r.data.some((p) => p.product_id === id));
    }).catch(() => {});
  }, [user, id]);

  const toggleWish = async () => {
    if (!user) { toast.error("Sign in to save"); return; }
    if (wish) {
      await api.delete(`/wishlist/${id}`);
      setWish(false);
      toast.success("Removed from wishlist");
    } else {
      await api.post(`/wishlist/${id}`);
      setWish(true);
      toast.success("Added to wishlist");
    }
  };

  useEffect(() => {
    if (!product) return;
    if (product.sizes?.length && !size) setSize(product.sizes[0]);
    if (product.colors?.length && !color) setColor(product.colors[0].name);
  }, [product]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdd = async () => {
    if (product.sizes?.length && !size) { toast.error("Select a size"); return; }
    if (product.colors?.length && !color) { toast.error("Select a color"); return; }
    const ok = await add({ product_id: product.product_id, quantity: qty, size, color });
    if (ok) setJustAdded(true);
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to review"); return; }
    try {
      await api.post("/reviews", { product_id: id, rating, comment });
      setComment("");
      toast.success("Review posted");
      load();
    } catch {
      toast.error("Could not post review");
    }
  };

  if (!product) {
    return <div className="max-w-[1400px] mx-auto px-6 py-20 text-center">Loading…</div>;
  }

  const images = (product.images?.length ? product.images : ["https://images.unsplash.com/photo-1611078489935-0cb964de46d6?w=1200"]).map((u) => resolveImage(u) || u);
  const isPos = product.source === "pos";

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10 w-full">
      <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 min-w-0">
        <div className="min-w-0">
          <div className="aspect-square border border-neutral-200 bg-white overflow-hidden flex items-center justify-center">
            <img src={images[activeImg]} alt={product.title} className="max-w-full max-h-full w-full h-full object-contain" />
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-2">
              {images.map((src, i) => (
                <button
                  key={src || i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square border overflow-hidden bg-white flex items-center justify-center ${activeImg === i ? "border-black" : "border-neutral-200"}`}
                >
                  <img src={src} alt="" className="max-w-full max-h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
            {product.category}
          </div>
          <h1 className="font-display font-black text-xl sm:text-3xl lg:text-5xl tracking-tight leading-[1.1] break-words">{product.title}</h1>
          {isPos ? (
            <div className="mt-3 border border-black p-3 bg-neutral-50" data-testid="pos-merchant-card">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Store className="w-4 h-4 text-[#0033FF]" />
                <span className="font-display font-black text-base sm:text-lg">{product.merchant?.name || product.seller_name}</span>
                <span className="text-[10px] uppercase tracking-widest font-bold bg-black text-white px-1.5 py-0.5">POS</span>
              </div>
              {product.merchant?.address && <div className="text-xs text-neutral-600 mt-1">{product.merchant.address}</div>}
              {product.merchant?.phone && <div className="text-xs text-neutral-600">{product.merchant.phone}</div>}
              <div className={`text-[10px] uppercase tracking-widest font-bold mt-1.5 ${product.delivery_available ? "text-[#00A355]" : "text-neutral-500"}`}>
                {product.delivery_available ? "Delivery available" : "Pickup — arrange with merchant"}
              </div>
            </div>
          ) : (
          <Link
            to={`/shop/${product.seller_id}`}
            data-testid="product-seller-link"
            className="inline-flex items-center gap-1.5 mt-3 group/seller"
          >
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">Sold by</span>
            <span
              className="font-display italic font-black text-base sm:text-xl lg:text-2xl text-[#FF2A00] underline decoration-2 underline-offset-4 decoration-[#FF2A00]/30 group-hover/seller:decoration-[#FF2A00] transition-all break-words"
            >
              {product.seller_name}
            </span>
            <span className="text-[#FF2A00] text-base sm:text-lg transition-transform group-hover/seller:translate-x-0.5">→</span>
          </Link>
          )}
          <div className="flex items-center gap-3 mt-4 text-sm">
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-4 h-4 ${i < Math.round(product.rating || 0) ? "fill-black stroke-black" : "stroke-neutral-300"}`} />
              ))}
            </div>
            <span className="text-neutral-600">{product.reviews_count} reviews</span>
            {product.trending && (
              <span data-testid="trending-pill" className="inline-flex items-center gap-1 bg-[#FF2A00] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">
                <Flame className="w-3 h-3" /> Trending this week
              </span>
            )}
          </div>
          <div className="font-display font-black text-2xl sm:text-4xl mt-5 sm:mt-6 break-words">{fmt(product.price)}</div>
          <ProductDescription text={product.description} />

          {product.sizes?.length > 0 && (
            <div className="mt-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.2em] font-bold">Size</span>
                <span className="text-xs text-neutral-500">{size || "Select"}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-${s}`}
                    onClick={() => setSize(s)}
                    className={`min-w-[44px] h-10 px-3 border text-sm font-semibold transition-colors ${
                      size === s
                        ? "bg-black text-white border-black"
                        : "bg-white text-black border-neutral-300 hover:border-black"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-[0.2em] font-bold">Color</span>
                <span className="text-xs text-neutral-500">{color || "Select"}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c.name}
                    data-testid={`color-${c.name}`}
                    onClick={() => setColor(c.name)}
                    title={c.name}
                    className={`w-10 h-10 border-2 grid place-items-center transition-all ${
                      color === c.name ? "border-black scale-105" : "border-neutral-200 hover:border-neutral-500"
                    }`}
                  >
                    <span
                      className="w-6 h-6 border border-neutral-300"
                      style={{ background: c.hex }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-3">
            <div className="flex items-center border border-black">
              <button data-testid="qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-12 font-bold text-lg">−</button>
              <div className="w-10 h-12 grid place-items-center font-semibold" data-testid="qty-value">{qty}</div>
              <button data-testid="qty-inc" onClick={() => setQty(qty + 1)} className="w-10 h-12 font-bold text-lg">+</button>
            </div>
            {isPos ? (
              <Button
                data-testid="pos-order-btn"
                onClick={() => { if (!user) { toast.error("Sign in to order"); return; } setPosOrderOpen(true); }}
                disabled={product.stock <= 0}
                className="flex-1 h-12 rounded-none bg-[#0033FF] hover:bg-[#0022CC] font-semibold"
              >
                {product.stock > 0 ? "Order now — direct from merchant" : "Out of stock"}
              </Button>
            ) : (
            <>
            <Button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              disabled={product.stock <= 0}
              className="flex-1 h-12 rounded-none bg-[#0033FF] hover:bg-[#0022CC] font-semibold"
            >
              {product.stock > 0 ? "Add to cart" : "Out of stock"}
            </Button>
            <Button
              data-testid="wishlist-toggle-btn"
              onClick={toggleWish}
              variant="outline"
              className="h-12 rounded-none border-black"
            >
              <Heart className={`w-5 h-5 ${wish ? "fill-[#0033FF] stroke-[#0033FF]" : ""}`} />
            </Button>
            <Button
              data-testid="share-product-btn"
              variant="outline"
              title="Share — best previews on WhatsApp / Facebook"
              className="h-12 rounded-none border-black"
              onClick={async () => {
                const shareUrl = `${window.location.origin}/api/share/products/${product.product_id}`;
                const shareData = {
                  title: product.title,
                  text: `${product.title} on Guyana Discounts`,
                  url: shareUrl,
                };
                if (navigator.share) {
                  try { await navigator.share(shareData); } catch { /* user cancelled */ }
                } else {
                  try {
                    await navigator.clipboard.writeText(shareUrl);
                    toast.success("Link copied — paste it on WhatsApp / Facebook");
                  } catch { /* ignore */ }
                }
              }}
            >
              <Share2 className="w-5 h-5" />
            </Button>
            </>
            )}
          </div>
          <div className="mt-3 text-xs text-neutral-500 flex items-center gap-2">
            <Check className="w-3.5 h-3.5" /> {product.stock} in stock
          </div>

          {justAdded && (
            <div
              data-testid="post-add-actions"
              className="mt-4 grid grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-1 duration-200"
            >
              <Link
                to="/cart"
                data-testid="post-add-proceed-to-cart"
                className="h-12 flex items-center justify-center gap-2 bg-black text-white font-semibold uppercase tracking-wide text-sm border border-black hover:bg-neutral-900 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" /> Proceed to Cart
              </Link>
              <Link
                to={`/shop/${product.seller_id}`}
                data-testid="post-add-continue-shopping"
                className="h-12 flex items-center justify-center gap-2 bg-white text-black font-semibold uppercase tracking-wide text-sm border border-black hover:bg-neutral-50 transition-colors"
              >
                <Store className="w-4 h-4" /> Continue Shopping
              </Link>
            </div>
          )}

          {canSeeAffiliateUI(user) && product.affiliate_commission_pct > 0 && (
            <ShareToEarnBlock product={product} />
          )}

          {product.seller_whatsapp && (
            <div className="mt-4">
              <WhatsAppButton
                number={product.seller_whatsapp}
                label={product.title}
                shareUrl={`${window.location.origin}/api/share/products/${product.product_id}`}
                className="w-full"
              />
              <p className="mt-2 text-xs text-neutral-500">
                Chat directly with <b>{product.seller_name}</b> — the message will include this product link.
              </p>
            </div>
          )}

          {product.external_link && (
            <a
              data-testid="external-link-compare-price-banner"
              href={product.external_link}
              target="_blank"
              rel="noopener noreferrer nofollow"
              onClick={() => {
                try {
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/external-click`;
                  if (navigator.sendBeacon) {
                    navigator.sendBeacon(url);
                  } else {
                    fetch(url, { method: "POST", keepalive: true }).catch(() => {});
                  }
                } catch { /* never block navigation */ }
              }}
              className="mt-4 group block bg-[#FFE600] text-black border-2 border-black px-3 py-3 text-center font-display font-black uppercase tracking-wider text-sm sm:text-base shadow-[4px_4px_0_0_#000] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[3px_3px_0_0_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-[2px_2px_0_0_#000] transition-all duration-100"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <ArrowRight className="w-4 h-4 shrink-0 animate-pulse" />
                <span>Click the link to compare price</span>
                <ArrowLeft className="w-4 h-4 shrink-0 animate-pulse" />
              </span>
            </a>
          )}

          {product.external_link && product.external_link_image && (() => {
            const isFavicon = /google\.com\/s2\/favicons|\.ico(\?|$)/i.test(product.external_link_image);
            const trackClick = () => {
              try {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.product_id}/external-click`;
                if (navigator.sendBeacon) {
                  navigator.sendBeacon(url);
                } else {
                  fetch(url, { method: "POST", keepalive: true }).catch(() => {});
                }
              } catch { /* never block navigation */ }
            };
            return (
              <a
                data-testid="product-external-link"
                href={product.external_link}
                target="_blank"
                rel="noopener noreferrer nofollow"
                onClick={trackClick}
                className="mt-4 block group border border-black bg-white hover:bg-neutral-50 transition-colors"
              >
                {product.external_link_label && (
                  <div className="px-3 py-2 text-[11px] uppercase tracking-widest font-bold border-b border-black flex items-center justify-between">
                    <span className="truncate pr-2">{product.external_link_label}</span>
                    <span className="text-[#0033FF] group-hover:translate-x-0.5 transition-transform shrink-0">→</span>
                  </div>
                )}
                {isFavicon ? (
                  <div className="flex items-center gap-3 px-4 py-4">
                    <img
                      src={product.external_link_image}
                      alt={product.external_link_label || "External link"}
                      className="w-10 h-10 object-contain shrink-0"
                      referrerPolicy="no-referrer"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{(() => { try { return new URL(product.external_link).hostname.replace(/^www\./, ""); } catch { return product.external_link; } })()}</div>
                      <div className="text-xs text-neutral-500">Tap to open in a new tab</div>
                    </div>
                  </div>
                ) : (
                  <img
                    src={product.external_link_image}
                    alt={product.external_link_label || "External link preview"}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="w-full h-auto object-cover max-h-[420px]"
                    onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                  />
                )}
              </a>
            );
          })()}

          {product.video_url && <ProductVideo url={product.video_url} />}

          <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-8 sm:mt-10 border-t border-neutral-200 pt-5 sm:pt-6 text-[11px] sm:text-xs">
            <div className="flex flex-col items-start gap-1"><Truck className="w-4 h-4" /><span className="font-semibold leading-tight">Free shipping<br className="sm:hidden" /><span className="hidden sm:inline"> </span>{fmt(20000)}+</span></div>
            <div className="flex flex-col items-start gap-1"><ShieldCheck className="w-4 h-4" /><span className="font-semibold leading-tight">Vetted seller</span></div>
            <div className="flex flex-col items-start gap-1"><RotateCcw className="w-4 h-4" /><span className="font-semibold leading-tight">{fmt(2000)}<br className="sm:hidden" /><span className="hidden sm:inline"> </span>returns</span></div>
          </div>
        </div>
      </div>

      {/* Reviews — native products only */}
      {!isPos && (
      <div className="mt-12 sm:mt-16 lg:mt-20 border-t border-neutral-200 pt-8 sm:pt-12 grid lg:grid-cols-[1fr_1fr] gap-8 sm:gap-12">
        <div>
          <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">Reviews</h2>
          <div className="mt-6 space-y-6">
            {reviews.length === 0 && <p className="text-sm text-neutral-500">No reviews yet.</p>}
            {reviews.map((r) => (
              <div key={r.review_id} className="border-b border-neutral-200 pb-6" data-testid={`review-${r.review_id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{r.user_name}</span>
                  <div className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? "fill-black stroke-black" : "stroke-neutral-300"}`} />
                    ))}
                  </div>
                </div>
                <ReviewComment text={r.comment} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-display font-black text-xl tracking-tight">Leave a review</h3>
          {user ? (
            <form onSubmit={submitReview} className="mt-4 space-y-4">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    type="button"
                    key={n}
                    data-testid={`rating-${n}`}
                    onClick={() => setRating(n)}
                    className="p-1"
                  >
                    <Star className={`w-6 h-6 ${n <= rating ? "fill-[#0033FF] stroke-[#0033FF]" : "stroke-neutral-400"}`} />
                  </button>
                ))}
              </div>
              <Textarea
                data-testid="review-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                required
                placeholder="Share your experience"
                className="rounded-none border-black"
              />
              <Button data-testid="submit-review-btn" type="submit" className="rounded-none bg-black hover:bg-neutral-800">
                Post review
              </Button>
            </form>
          ) : (
            <p className="mt-4 text-sm text-neutral-500">Sign in to leave a review.</p>
          )}
        </div>
      </div>
      )}

      {/* Recommendations — driven by view + conversion data */}
      {recs.length > 0 && (
        <section className="mt-20 border-t border-neutral-200 pt-12" data-testid="recommendations">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
            <h2 className="font-display font-black text-3xl tracking-tight">
              You might also like
            </h2>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
              Picked by shopper behavior
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {recs.map((r) => (
              <RecommendationCard key={r.product_id} product={r} onQuickAdd={() => add({ product_id: r.product_id, quantity: 1, source: "rec_product" })} />
            ))}
          </div>
        </section>
      )}

      {product.seller_whatsapp && (
        <WhatsAppFloating
          number={product.seller_whatsapp}
          label={product.title}
          shareUrl={`${window.location.origin}/api/share/products/${product.product_id}`}
        />
      )}

      {isPos && posOrderOpen && (
        <PosOrderModal
          product={product}
          qty={qty}
          onClose={() => setPosOrderOpen(false)}
        />
      )}
    </div>
  );
}

function ProductDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const desc = text || "";
  const isLong = desc.length > 320 || desc.split("\n").length > 4;
  if (!desc) return null;
  if (!isLong) {
    return (
      <p className="text-[15px] sm:text-sm text-neutral-700 mt-5 sm:mt-6 leading-relaxed whitespace-pre-wrap break-words" data-testid="product-description">
        {desc}
      </p>
    );
  }
  return (
    <div className="mt-5 sm:mt-6" data-testid="product-description">
      <div
        className={`relative text-[15px] sm:text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap break-words overflow-hidden transition-[max-height] duration-500 ease-in-out ${
          expanded ? "max-h-[2000px]" : "max-h-32"
        }`}
      >
        {desc}
        {!expanded && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      <button
        data-testid="description-toggle"
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mt-2 inline-flex items-center gap-1.5 min-h-[44px] py-2 text-sm font-semibold text-[#0033FF] hover:underline"
      >
        {expanded ? "Show less" : "Click to see more"}
        <span className={`inline-block transition-transform ${expanded ? "rotate-180" : ""}`}>▾</span>
      </button>
    </div>
  );
}

function PosOrderModal({ product, qty, onClose }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.whatsapp || "");
  const [address, setAddress] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const total = (product.price || 0) * qty;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { toast.error("Name and phone are required"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/pos/order", {
        listing_id: product.product_id.replace(/^pos_/, ""),
        quantity: qty,
        customer_name: name.trim(),
        customer_phone: phone.trim(),
        delivery_address: address.trim(),
      });
      setPlaced(data.order);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place the order — try again");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="pos-order-modal">
      <div className="bg-white border border-black w-full sm:max-w-md p-5 sm:p-6" onClick={(e) => e.stopPropagation()}>
        {placed ? (
          <div data-testid="pos-order-success">
            <div className="font-display font-black text-2xl">Order placed</div>
            <p className="text-sm text-neutral-600 mt-2">
              {product.merchant?.name || "The merchant"} has your order and will confirm shortly.
            </p>
            <div className="mt-4 border border-neutral-200 p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-neutral-500">Order</span><span className="font-mono text-xs">{String(placed.id || "").slice(0, 8)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Item</span><span className="font-semibold truncate ml-3">{product.title} × {placed.quantity ?? qty}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Total</span><span className="font-display font-black">{fmt(placed.total ?? total)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Status</span><span className="uppercase text-[10px] font-bold tracking-widest bg-[#00A355] text-white px-1.5 py-0.5">{placed.status || "placed"}</span></div>
            </div>
            <Button data-testid="pos-order-done" onClick={onClose} className="mt-4 w-full h-12 rounded-none bg-black text-white font-semibold hover:bg-neutral-800">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="pos-order-form">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-black text-2xl">Order from merchant</div>
                <div className="text-xs text-neutral-500 mt-1">{product.title} × {qty} — <span className="font-bold text-black">{fmt(total)}</span></div>
              </div>
              <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black text-xl leading-none">×</button>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">Your name</label>
                <input data-testid="pos-order-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 h-11 px-3 border border-black rounded-none text-sm" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">WhatsApp / phone</label>
                <input data-testid="pos-order-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full mt-1 h-11 px-3 border border-black rounded-none text-sm" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-neutral-700">Delivery address</label>
                <textarea data-testid="pos-order-address" value={address} onChange={(e) => setAddress(e.target.value)} rows={2} placeholder={product.delivery_available ? "Where should it be delivered?" : "Your address (pickup may be arranged)"} className="w-full mt-1 px-3 py-2 border border-black rounded-none text-sm" />
              </div>
            </div>
            <Button
              data-testid="pos-order-submit"
              type="submit"
              disabled={placing}
              className="mt-4 w-full h-12 rounded-none bg-[#0033FF] hover:bg-[#0022CC] font-semibold disabled:opacity-50"
            >
              {placing ? "Placing order…" : `Place order — ${fmt(total)}`}
            </Button>
            <p className="text-[11px] text-neutral-500 mt-2">The order reserves stock in the merchant's register. They'll contact you on the number above.</p>
          </form>
        )}
      </div>
    </div>
  );
}


function ReviewComment({ text }) {
  const [expanded, setExpanded] = useState(false);
  const comment = text || "";
  const isLong = comment.length > 220 || comment.split("\n").length > 3;
  if (!comment) return null;
  if (!isLong) {
    return <p className="mt-2 text-sm text-neutral-700 whitespace-pre-wrap">{comment}</p>;
  }
  return (
    <div className="mt-2">
      <div
        className={`relative text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap overflow-hidden transition-[max-height] duration-500 ease-in-out ${
          expanded ? "max-h-[2000px]" : "max-h-20"
        }`}
      >
        {comment}
        {!expanded && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#0033FF] hover:underline"
      >
        {expanded ? "Show less" : "Read more"}
        <span className={`inline-block transition-transform ${expanded ? "rotate-180" : ""}`}>▾</span>
      </button>
    </div>
  );
}

function RecommendationCard({ product, onQuickAdd }) {
  const img = product.images?.[0] || "";
  const { user } = useAuth();
  const showAffiliate = canSeeAffiliateUI(user);
  const trackClick = () => {
    api.post("/rec/click", { product_id: product.product_id, source: "rec_product" }).catch(() => {});
  };
  return (
    <div className="group" data-testid={`rec-${product.product_id}`}>
      <Link to={`/products/${product.product_id}`} onClick={trackClick} className="block">
        <div className="aspect-square bg-neutral-100 border border-neutral-200 overflow-hidden relative">
          {img && (
            <img
              src={img}
              alt={product.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          {showAffiliate && product.affiliate_commission_pct > 0 && (() => {
            const earn = Math.round((product.price || 0) * product.affiliate_commission_pct / 100);
            return (
              <span className="absolute top-2 left-2 bg-[#FFE600] text-black text-[11px] font-display font-black uppercase tracking-wide px-2 py-1 shadow-[2px_2px_0_0_#000]">
                Earn {earn > 0 ? fmt(earn) : `${product.affiliate_commission_pct}%`}
              </span>
            );
          })()}
          {product.trending && (
            <span className={`absolute ${showAffiliate && product.affiliate_commission_pct > 0 ? "top-10" : "top-2"} left-2 bg-[#FF2A00] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1`}>
              <Flame className="w-3 h-3" /> Trending
            </span>
          )}
        </div>
        <div className="mt-3">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/shop/${product.seller_id}`; }}
            className="text-[10px] uppercase tracking-widest text-[#FF2A00] font-bold hover:underline"
          >
            {product.seller_name} →
          </button>
          <div className="font-display font-bold text-sm line-clamp-2 mt-0.5">{product.title}</div>
          <div className="font-display font-black text-base mt-1">{fmt(product.price)}</div>
        </div>
      </Link>
      <button
        data-testid={`rec-add-${product.product_id}`}
        onClick={(e) => { e.preventDefault(); onQuickAdd(); }}
        className="mt-2 w-full h-9 bg-black hover:bg-neutral-800 text-white text-xs uppercase tracking-widest font-semibold transition-colors"
      >
        + Add to cart
      </button>
    </div>
  );
}
